import { Injectable, ApplicationRef, EnvironmentInjector, createComponent, ComponentRef } from '@angular/core';
import { Observable, from } from 'rxjs';
import { MetroConfirmationComponent } from '../components/metro-confirmation/metro-confirmation.component';

export interface ConfirmationOptions {
  title: string;
  message: string;
  type?: 'danger' | 'warning' | 'info' | 'success';
  confirmText?: string;
  cancelText?: string;
  showConfirmButton?: boolean;
  showCancelButton?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ConfirmationModalService {
  constructor(
    private appRef: ApplicationRef,
    private environmentInjector: EnvironmentInjector
  ) {}

  confirm(options: ConfirmationOptions): Observable<boolean> {
    return from(this.openModal({
      ...options,
      type: options.type ?? 'danger',
      showConfirmButton: options.showConfirmButton ?? true,
      showCancelButton: options.showCancelButton ?? false
    }));
  }

  info(message: string, title: string, opts: Partial<ConfirmationOptions> = {}): Observable<boolean> {
    return this.confirm({ title, message, type: 'info', ...opts });
  }

  success(message: string, title: string, opts: Partial<ConfirmationOptions> = {}): Observable<boolean> {
    return this.confirm({ title, message, type: 'success', ...opts });
  }

  warning(message: string, title: string, opts: Partial<ConfirmationOptions> = {}): Observable<boolean> {
    return this.confirm({ title, message, type: 'warning', ...opts });
  }

  error(message: string, title: string, opts: Partial<ConfirmationOptions> = {}): Observable<boolean> {
    return this.confirm({ title, message, type: 'danger', ...opts });
  }

  private openModal(options: ConfirmationOptions): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const componentRef = createComponent(MetroConfirmationComponent, {
        environmentInjector: this.environmentInjector
      });

      const instance = componentRef.instance;
      instance.title = options.title;
      instance.message = options.message;
      instance.type = options.type ?? 'danger';
      instance.confirmText = options.confirmText ?? 'Aceptar';
      instance.cancelText = options.cancelText ?? 'Cancelar';

      const showConfirm = options.showConfirmButton ?? true;
      const showCancel = options.showCancelButton ?? false;

      if (!showConfirm) instance.confirmText = '';
      if (!showCancel) instance.cancelText = '';

      instance.confirmed.subscribe((value: boolean) => {
        this.destroy(componentRef);
        resolve(value);
      });

      this.appRef.attachView(componentRef.hostView);
      const domElem = (componentRef.hostView as any).rootNodes[0] as HTMLElement;
      document.body.appendChild(domElem);
    });
  }

  private destroy(componentRef: ComponentRef<MetroConfirmationComponent>) {
    this.appRef.detachView(componentRef.hostView);
    componentRef.destroy();
  }
}
